import { 
  Calendar, 
  Building2, 
  FolderPlus, 
  Coins, 
  Target, 
  Upload, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  Download,
  FileSpreadsheet
} from "lucide-react";

export interface GettingStartedStep {
  id: number;
  title: string;
  shortTitle: string;
  description: string;
  detail: string;
  icon: any;
  route: string;
  estimatedTime?: string;
  getCompleted: (data: GettingStartedData) => boolean;
  getEnabled: (data: GettingStartedData) => boolean;
  mockDownloads?: {
    label: string;
    filename: string;
    url: string;
  }[];
}

export interface GettingStartedData {
  hasFundCalendars: boolean;
  hasFunds: boolean;
  hasAccounts: boolean;
  hasInstruments: boolean;
  hasFundSelected: boolean;
  hasTransactions: boolean;
  hasHoldings: boolean;
  hasValidatedNavPack: boolean;
  hasClosedPeriod: boolean;
  hasResolvedExceptions: boolean;
}

export const GETTING_STARTED_STEPS: GettingStartedStep[] = [
  {
    id: 1,
    title: "Create a fund calendar",
    shortTitle: "Calendar",
    description: "Define fiscal structure and set up reporting periods",
    detail: "Fund calendars are the foundation of your reporting structure. Set up your fiscal year and reporting period definitions.",
    icon: Calendar,
    route: "/app/fund-calendars",
    estimatedTime: "5 min",
    getCompleted: (data) => data.hasFundCalendars,
    getEnabled: () => true,
  },
  {
    id: 2,
    title: "Upload funds",
    shortTitle: "Funds",
    description: "Upload your investment funds to the platform",
    detail: "Upload funds using the sample CSV. Map Legal Entity ID/Fund to fundKey and Legal Entity to name. You'll create the import template as part of this process.",
    icon: Building2,
    route: "/app/imports?type=funds",
    estimatedTime: "10 min",
    getCompleted: (data) => data.hasFunds,
    getEnabled: (data) => data.hasFundCalendars,
    mockDownloads: [
      {
        label: "Funds CSV Template",
        filename: "fund.csv",
        url: "/mock-data/funds.csv"
      }
    ]
  },
  {
    id: 3,
    title: "Upload accounts",
    shortTitle: "Accounts",
    description: "Import your chart of accounts structure",
    detail: "Upload chart of accounts using the sample CSV. Map Account Code → accountKey, Account Name → name, Parent Code → parentAccountKey.",
    icon: FolderPlus,
    route: "/app/imports?type=accounts",
    estimatedTime: "5 min",
    getCompleted: (data) => data.hasAccounts,
    getEnabled: (data) => data.hasFunds,
    mockDownloads: [
      {
        label: "Chart of Accounts Template",
        filename: "accounts.csv",
        url: "/mock-data/accounts.csv"
      }
    ]
  },
  {
    id: 4,
    title: "Upload instruments",
    shortTitle: "Instruments",
    description: "Extract and upload financial instruments from transactions",
    detail: "Extract instruments from the file use the Instruments template.",
    icon: Coins,
    route: "/app/imports?type=instruments",
    estimatedTime: "8 min",
    getCompleted: (data) => data.hasInstruments,
    getEnabled: (data) => data.hasAccounts,
    mockDownloads: [
      {
        label: "Instruments",
        filename: "instruments.csv",
        url: "/mock-data/instruments.csv"
      }
    ]
  },
  {
    id: 5,
    title: "Select a fund and access imports",
    shortTitle: "Select Fund",
    description: "Choose your working fund, then click Import at top right",
    detail: "Set your active fund context, then access the import functionality to upload transactions and holdings data.",
    icon: Target,
    route: "/app/funds",
    estimatedTime: "2 min",
    getCompleted: (data) => data.hasFundSelected,
    getEnabled: (data) => data.hasInstruments,
  },
  {
    id: 6,
    title: "Import transactions",
    shortTitle: "Transactions",
    description: "Upload transaction data for your fund",
    detail: "Import April transactions. Map transaction_date → transactionDate, base_amount → baseAmount, currency → currency, quantity → quantity, Account Code → accountKey.",
    icon: Upload,
    route: "/app/imports?type=transactions",
    estimatedTime: "10 min",
    getCompleted: (data) => data.hasTransactions,
    getEnabled: (data) => data.hasFundSelected,
    mockDownloads: [
        {
          label: "April Transactions",
          filename: "April_transactions.csv",
          url: "/mock-data/April_transactions.csv"
        },
        {
          label: "May Transactions",
          filename: "May_transactions.csv",
          url: "/mock-data/May_transactions.csv"
        },
        {
          label: "June Transactions",
          filename: "June_transactions.csv",
          url: "/mock-data/June_transactions.csv"
        }
    ]
  },
  {
    id: 7,
    title: "Import holdings",
    shortTitle: "Holdings",
    description: "Upload holdings data for your fund",
    detail: "Import April holdings. Map total_base_amount → baseAmount, currency → currency, total_quantity → quantity, Account Code → accountKey, narrative → instrumentKey.",
    icon: FileSpreadsheet,
    route: "/app/imports?type=holdings",
    estimatedTime: "8 min",
    getCompleted: (data) => data.hasHoldings,
    getEnabled: (data) => data.hasTransactions,
    mockDownloads: [
      {
        label: "April Holdings",
        filename: "April_holdings.csv",
        url: "/mock-data/April_holdings.csv"
      },
      {
        label: "May Holdings",
        filename: "May_holdings.csv",
        url: "/mock-data/May_holdings.csv"
      },
      {
        label: "June Holdings",
        filename: "June_holdings.csv",
        url: "/mock-data/June_holdings.csv"
      }
    ]
  },
  {
    id: 8,
    title: "Validate NAV pack and close period",
    shortTitle: "Validate & Close",
    description: "Validate data in /app/transactions and /app/holdings, then close the period",
    detail: "Review all transactions and holdings for accuracy. Go to fund dashboard → click reporting period at top → close it. This will auto-open a new period. Then upload data for another month to trigger validation rules.",
    icon: CheckCircle2,
    route: "/app/transactions",
    estimatedTime: "15 min",
    getCompleted: (data) => data.hasClosedPeriod,
    getEnabled: (data) => data.hasHoldings,
  },
  {
    id: 9,
    title: "Resolve exceptions",
    shortTitle: "Exceptions",
    description: "Review and resolve validation exceptions",
    detail: "After uploading additional month's data, validation rules will generate exceptions. Visit the exceptions page to review and resolve these issues.",
    icon: AlertTriangle,
    route: "/app/exceptions",
    estimatedTime: "10 min",
    getCompleted: (data) => data.hasResolvedExceptions,
    getEnabled: (data) => data.hasClosedPeriod,
  }
]; 